/**
 * Save a file through the browser's Save As box: the user picks the folder and
 * name, then the file is written there. Chrome and Edge have the box
 * (showSaveFilePicker). Firefox and Safari don't, so they download it as
 * before, where their own "ask where to save" setting still applies.
 *
 * The box only opens straight after a click (about five seconds in Chrome),
 * and an Excel or Word export can take longer than that to build. So it asks
 * first and builds the file after: `source` may be a function, called once a
 * place has been picked.
 */

export type SaveOutcome = 'saved' | 'cancelled' | 'downloaded';

export interface SaveResult {
  outcome: SaveOutcome;
  /** The name it was saved under; the user can change it in the box. */
  name: string;
}

interface PickerType {
  description: string;
  accept: Record<string, string[]>;
}

interface Writable {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}

/** The parts of FileSystemFileHandle used here (not in every TS lib). */
export interface SaveFileHandle {
  name: string;
  createWritable(): Promise<Writable>;
  remove?(): Promise<void>;
}

type Picker = (options: { suggestedName?: string; types?: PickerType[] }) => Promise<SaveFileHandle>;

const TYPES: Record<string, { description: string; mime: string }> = {
  pdf: { description: 'PDF document', mime: 'application/pdf' },
  xlsx: { description: 'Excel workbook', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  docx: { description: 'Word document', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  csv: { description: 'CSV file', mime: 'text/csv' },
};

/** The box's "Save as type" list, so the right extension is kept. */
export function pickerTypes(fileName: string): PickerType[] | undefined {
  const ext = /\.([a-z0-9]+)$/i.exec(fileName)?.[1]?.toLowerCase();
  const type = ext ? TYPES[ext] : undefined;
  return type ? [{ description: type.description, accept: { [type.mime]: [`.${ext}`] } }] : undefined;
}

/** The Save As box, or null where there isn't one (or it would be refused, as in a frame). */
export function savePicker(win: Window = window): Picker | null {
  const picker = (win as unknown as { showSaveFilePicker?: Picker }).showSaveFilePicker;
  if (typeof picker !== 'function') return null;
  try {
    if (win.self !== win.top) return null;
  } catch {
    return null;
  }
  return picker.bind(win);
}

/** The plain download: an <a download> click. */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function isAbort(e: unknown) {
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError';
}

/**
 * Saves `source` as `fileName`. Resolves 'cancelled' when the user closes the
 * box (nothing is built), 'saved' once it is written, 'downloaded' when it
 * fell back to a plain download. Rejects when `source` fails.
 *
 * Without the box and with the file already in hand, the download happens
 * before this returns, as a plain click handler's would.
 */
export function saveFile(
  fileName: string,
  source: Blob | (() => Promise<Blob>),
  options: { download?: (blob: Blob, fileName: string) => void; win?: Window } = {},
): Promise<SaveResult> {
  const download = options.download ?? downloadBlob;
  const make = typeof source === 'function' ? source : () => Promise.resolve(source);
  const picker = savePicker(options.win);

  if (!picker) {
    if (typeof source !== 'function') {
      download(source, fileName);
      return Promise.resolve({ outcome: 'downloaded', name: fileName });
    }
    return make().then((blob) => {
      download(blob, fileName);
      return { outcome: 'downloaded', name: fileName };
    });
  }

  return (async (): Promise<SaveResult> => {
    let handle: SaveFileHandle | null = null;
    try {
      handle = await picker({ suggestedName: fileName, types: pickerTypes(fileName) });
    } catch (e) {
      if (isAbort(e)) return { outcome: 'cancelled', name: fileName };
      // No recent click, a blocked folder: the plain download still works.
    }

    let blob: Blob;
    try {
      blob = await make();
    } catch (e) {
      // The box has already made an empty file; take it away again where the browser allows it.
      await handle?.remove?.().catch(() => undefined);
      throw e;
    }

    if (handle) {
      try {
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return { outcome: 'saved', name: handle.name };
      } catch {
        // Permission taken back, disk full: fall through to the download.
      }
    }
    download(blob, fileName);
    return { outcome: 'downloaded', name: fileName };
  })();
}
